import { PageContainer } from "components/core/basic/PageContainer";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "dates/DatesContext";
import { useDeferredValue, useMemo } from "react";
import { useAppSelector } from "redux/hooks";
import { useModuleEnabled } from "redux/selectors/modules";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS, TARGET_REPORT_BREADCRUMB_SPEC } from "utils/dashboardLinks";
import { useStandardEntityMetrics } from "views/merchandise/tables/metricUtils";
import { type TargetQueryV4Row } from "../api/target/v4TargetReportByTarget";
import { createColumnBuilder } from "components/table/columnBuilder";
import { css } from "../../../../styled-system/css";
import { idsToVisibilityState, selectionStateToSelectedIds } from "components/table/utils";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { useGetV4TargetReportByDate } from "../api/target/v4TargetReportByDate";
import { useQuery } from "@tanstack/react-query";
import { useGetV4TargetReportTotals } from "../api/target/v4TargetReportTotals";
import { buildMetric } from "views/merchandise/metrics";
import { COMPARE_METRIC_LOOKUP } from "views/merchandise/tables/metricsByEntity";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import TargetReportTable from "./TargetReportTable";
import { Flex } from "../../../../styled-system/jsx";
import { Nav } from "react-bootstrap";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import { TargetBadges, useAdvertisementReport } from "views/SearchTermReport";
import moment from "moment";
import { type GVAdvertisement } from "modules/advertising/types";
import { formatCurrency } from "utils/formatNumber";
import KeywordCell from "components/custom/category/advertisements/KeywordCell";
import TrellisLink from "components/core/basic/TrellisLink";
import { getDashboardTo } from "views/AdvertisingOverview";
import { type RowData } from "@tanstack/react-table";
import { useCategoriesQuery } from "hooks/useCategories";
import useQueryParams from "hooks/useQueryParams";
import { formatTargetText } from "utils/formatTargetText";

const columnHelper = createColumnBuilder<TargetQueryV4Row>();

const TargetReportBody = () => {
  const [targetType, setTargetType] = useQueryParams("tab", "keyword");

  const acosOrRoas = useAcosOrRoas();
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { start, end, group } = useDates();

  const standardMetricIds = useStandardEntityMetrics("target");

  const { data: adsData, isLoading: isAdsDataLoading } = useAdvertisementReport({
    start_date: moment().add(10, "days").format("YYYY-MM-DD"),
    end_date: moment().add(10, "days").format("YYYY-MM-DD"),
  });
  const categoriesQuery = useCategoriesQuery();

  const adsMapping: { [key: string]: GVAdvertisement } = useMemo(() => {
    return (adsData?.adplans ?? []).reduce(
      (acc: { [key: string]: GVAdvertisement }, row: GVAdvertisement) => {
        acc[row.ad_id.toString()] = row;
        return acc;
      },
      {} as { [key: string]: GVAdvertisement },
    );
  }, [adsData]);

  const adFilterOptions = useMemo(() => {
    return Object.values(adsMapping).map((ad) => ({ value: ad.ad_id, label: ad.ad_name }));
  }, [adsMapping]);

  const categoryFilterOptions = useMemo(
    () => [
      { value: "all", label: "All Categories" },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("text", {
        id: "text",
        header: `Text`,
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: (props) => {
          const { text, ad_id, refinements } = props.row.original;
          const adName = ad_id ? (adsMapping[ad_id]?.ad_name ?? "") : "";

          return (
            <div className={css({ paddingLeft: "small" })}>
              <KeywordCell
                value={formatTargetText(text, { ...refinements, marketPlace: marketPlace })}
                columnWidth={300}
              />
              <br />
              <TrellisLink
                to={getDashboardTo({
                  type: "advertisement",
                  id: ad_id,
                })}
                target={"_blank"}
                className={css({
                  paddingRight: "medium",
                  textDecoration: "none",
                  fontSize: "medium",
                  fontWeight: 500,
                })}
              >
                <small>{adName}</small>
              </TrellisLink>
              <br />
              <TargetBadges target={props.row.original} />
            </div>
          );
        },
      }),
      // Invisible columns to support filtering
      columnHelper.accessor((row: RowData) => row, {
        id: "ad_id",
        header: "Media Plan",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row: RowData) => row, {
        id: "category_id",
        header: "Category",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      // Invisible columns end
      columnHelper.accessor("match_type", {
        id: "match_type",
        header: "Match Type",
        cell: ({ getValue }) => {
          const value = getValue()
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .toLowerCase()
            .replace("_", " ");
          return <span>{value}</span>;
        },
        size: 100,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: "Target match type",
          },
        },
      }),
      columnHelper.accessor("bid", {
        id: "bid",
        header: "Bid",
        size: 70,
        cell: ({ getValue }) => {
          const bid = getValue();

          if (bid) {
            return formatCurrency(bid, marketPlace);
          }
          return null;
        },
        meta: {
          columnSelectorOptions: {
            grouping: "configuration",
            description: `The current bid for the advertised ${targetType}`,
          },
          columnFilterOptions: {
            format: "currency",
          },
        },
      }),
      columnHelper.accessor("state", {
        id: "state",
        header: "State",
        size: 100,
        cell: ({ getValue }) => getValue(),
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: `The serving status of the advertised ${targetType}`,
          },
        },
      }),
      columnHelper.accessor("ad_group_name", {
        id: "ad_group_name",
        header: "Ad Group",
        cell: ({ getValue }) => getValue(),
        meta: {
          columnSelectorOptions: {
            grouping: "configuration",
            description: `The ad group the ${targetType} belongs to`,
          },
        },
      }),
      columnHelper.accessor("creation_date", {
        id: "creation_date",
        header: "Created On",
        cell: ({ getValue }) => moment(getValue()).format("ll"),
        meta: {
          columnFilterOptions: {
            format: "date",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: `The ${targetType}'s creation date`,
          },
        },
      }),
      columnHelper.accessor("source", {
        id: "source",
        header: "Source",
        cell: ({ getValue }) => {
          const value = getValue();

          return (
            value === "GV_DISCOVERY" ? "initial"
            : value === "harvested" ? "harvesting"
            : value === "harvested_v2" ? "harvesting V2"
            : snakeCaseToSentenceCase(value).toLowerCase()
          );
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: "The source of the advertisement target.",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
    ],
    [standardMetricIds, adsMapping, marketPlace, targetType],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "text",
    "bid",
    "match_type",
    "state",
    "clicks",
    "ad_spend",
    "impressions",
    "orders",
    "ad_sales",
    "acos",
    "conversion_rate",
    "ctr",
    "cpc",
  ];

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      left: ["text"],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `advertised${toTitleCase(targetType)}Report`,
    initialState: initialStateConfig,
  });

  const deferredSearch = useDeferredValue(state.globalFilter);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);
  const selectedIdsFilter =
    selectedIds.length ?
      [{ id: targetType === "target" ? "adv_target_id" : "adv_keyword_id", value: { op: "anyof", value: selectedIds } }]
    : [];

  const byDateOptionsGetter = useGetV4TargetReportByDate(targetType ?? "keyword");
  const targetsByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: [...state.columnFilters, ...selectedIdsFilter],
      search: deferredSearch,
    }),
  );
  const totalOptionsGetter = useGetV4TargetReportTotals(targetType ?? "keyword");
  const targetTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters: [...state.columnFilters, ...selectedIdsFilter],
      search: deferredSearch,
    }),
  );

  const metrics = useMemo(() => {
    const data = {
      total: targetTotalsReportQuery.data?.data,
    };
    return standardMetricIds.map((metricId) =>
      buildMetric({ marketPlace: marketPlace }, data, metricId, COMPARE_METRIC_LOOKUP[metricId]),
    );
  }, [targetTotalsReportQuery.data?.data, standardMetricIds, marketPlace]);

  return (
    <PageContainer withBreadcrumb={true}>
      <MetricsCard
        id={"targetReport"}
        title={"Performance Overview"}
        data={{ date: targetsByDateReportQuery.data?.data ?? [], total: targetTotalsReportQuery.data?.data }}
        loading={
          (targetsByDateReportQuery.isFetching &&
            (!targetsByDateReportQuery.data || targetsByDateReportQuery.isPlaceholderData)) ||
          isAdsDataLoading
        }
        showLoadingSpinner
        showSkeletonLoadingEffect={
          (targetTotalsReportQuery.isFetching &&
            (!targetTotalsReportQuery.data || targetTotalsReportQuery.isPlaceholderData)) ||
          isAdsDataLoading
        }
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {start.format("MMMM Do YYYY")}
            {" - "}
            {end.format("MMMM Do YYYY")}
          </>
        }
        context={"target"}
      >
        <Flex className={css({ borderBottom: "1px solid #CCC" })}>
          <Nav
            variant={"underline"}
            activeKey={targetType ?? "keyword"}
            className={css({
              fontSize: "medium",
            })}
            onSelect={(tab: string | null) => {
              if (tab !== null) {
                setTargetType(tab);
              }
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey={"keyword"}>Keywords</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={"target"}>Targets</Nav.Link>
            </Nav.Item>
          </Nav>
        </Flex>
        <TargetReportTable
          targetType={targetType ?? "keyword"}
          state={state}
          initialState={initialState}
          setters={setters}
          key={targetType}
          adFilterOptions={adFilterOptions}
          categoryFilterOptions={categoryFilterOptions}
          isAdsDataLoading={isAdsDataLoading}
          adsMapping={adsMapping}
          columns={columns}
        />
      </MetricsCard>
    </PageContainer>
  );
};

const TargetReport = () => {
  const hasOrgSummaryreportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const hasTargetReportModule = useModuleEnabled("TARGET_REPORT");
  const breadCrumbItems = [
    {
      name: "Target Report",
      href: "/user/report/target",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: "Reports",
    },
  ];

  if (
    hasOrgSummaryreportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }

  if (
    hasTargetReportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === TARGET_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(TARGET_REPORT_BREADCRUMB_SPEC);
  }
  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <TargetReportBody />
    </DatesProvider>
  );
};

export default TargetReport;
